import {
  FC,
  JSX,
  useState
} from "react";
import {
  View,
  Text,
  ScrollView,
  ActivityIndicator,
} from "react-native";
import { router } from "expo-router";
import { toast } from "sonner-native";
import { MaskedTextInput } from "react-native-mask-text";
import { ICreatePersonReportResponse } from "@/_store/person/contracts";
import { useCreatePersonReportMutation } from "@/_store/person/person.slice";
import { BACKEND_DATE_FORMAT } from "@/_utils/formatDate";
import CustomButton from "@/app/components/CustomButton";
import CustomCheckbox from "@/app/components/CustomCheckbox";
import ErrIcon from "@/assets/icons/main/redAlert.svg";
import dayjs from "dayjs";
import IndividualCheckHeader from "@/app/(main)/general/individualCheck/IndividualCheckHeader";
import { usePlatformStyles } from "@/_hooks/usePlatformStyles";
import StyledTextInput from "@/app/components/StyledTextInput";
import useCustomBottomSheet from "@/_hooks/useBottomSheet";
import DatePickerField from "@/app/components/DatePickerFiled";

interface IIndividualCheckProps {
  totalReports: number;
}

export interface IIndividualCheckFormValues {
  full_name: string;
  birth: string;
  series: string;
  number: string;
  snils: string;
  driver_license: string;
  driver_license_date: string;
  consent: boolean;
}

const initialFormValue: IIndividualCheckFormValues = {
  full_name: "",
  birth: "",
  series: "",
  number: "",
  snils: "",
  driver_license: "",
  driver_license_date: "",
  consent: false
};

export const labelBlockClass: string = "flex-row items-start";
export const requiredLabelClass: string = "text-red-500 mr-1";
export const labelClass: string = "mb-2";
export const textInputClass: string = "border border-gray-300 rounded-2xl py-3 px-4 mb-6 text-base outline-none";

const IndividualCheckForm: FC<IIndividualCheckProps> = ({ totalReports }): JSX.Element => {
  const [form, setForm] = useState<IIndividualCheckFormValues>(initialFormValue);
  const { webOutlineNone } = usePlatformStyles();
  const { closeSheet } = useCustomBottomSheet();
  const [createPersonReport, { isLoading: isCreatePersonReportLoading }] = useCreatePersonReportMutation();

  const {
    snils,
    series,
    number,
    consent,
    full_name,
    birth,
    driver_license,
    driver_license_date,
  } = form;

  const isButtonDisabled: boolean = !full_name || !birth || !consent;

  const handleChange = (key: keyof IIndividualCheckFormValues, value: string | boolean): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (): Promise<void> => {
    try {
      if (isButtonDisabled) return;

      const createPromise: ReturnType<typeof createPersonReport> = createPersonReport({
        full_name,
        birth: dayjs(birth)?.format(BACKEND_DATE_FORMAT),
        ...(series && { series }),
        ...(number && { number }),
        ...(snils && { snils }),
        ...(driver_license && { driver_license }),
        ...(driver_license_date && {driver_license_date: dayjs(driver_license_date).format(BACKEND_DATE_FORMAT)}),
      });

      const { id }: ICreatePersonReportResponse = await createPromise.unwrap();

      router.push(`/(main)/general/successUserCheck?id=${id}`);
      closeSheet();
    } catch (err: any) {
      const errorMessage = err?.data?.error
        || err?.data?.full_name
        || "При запросе отчета произошла ошибка. Пожалуйста, попробуйте снова позже.";

      console.log(err);
      toast.error(errorMessage, { icon: <ErrIcon /> })
    }
  };

  return (
    <ScrollView className="pb-32">
      {!isCreatePersonReportLoading ? (
        <>
          <IndividualCheckHeader totalReports={totalReports} />
          <View className={labelBlockClass}>
            <Text className={requiredLabelClass}>*</Text>
            <Text className={labelClass}>ФИО</Text>
          </View>
          <StyledTextInput
            className={textInputClass}
            placeholder="Введите ФИО"
            value={full_name}
            style={webOutlineNone}
            onChangeText={(text: string) => handleChange("full_name", text)}
          />
          <View className={labelBlockClass}>
            <Text className={requiredLabelClass}>*</Text>
            <Text className={labelClass}>Дата рождения</Text>
          </View>
          <DatePickerField
            placeholder="Введите дату рождения"
            className={textInputClass}
            value={birth}
            onChange={(value) => handleChange("birth", value)}
          />
          <View className="flex-row gap-3">
            <View className="flex-1">
              <Text className={labelClass}>Серия</Text>
              <MaskedTextInput
                mask="9999"
                className={textInputClass}
                placeholder="0000"
                keyboardType="numeric"
                maxLength={4}
                style={webOutlineNone}
                value={series}
                onChangeText={(_: string, unmasked: string) => handleChange("series", unmasked)}
              />
            </View>
            <View className="flex-1">
              <Text className={labelClass}>Номер</Text>
              <MaskedTextInput
                mask="999999"
                className={textInputClass}
                placeholder="000000"
                keyboardType="numeric"
                style={webOutlineNone}
                maxLength={6}
                value={number}
                onChangeText={(_: string, unmasked: string) => handleChange("number", unmasked)}
              />
            </View>
          </View>
          <Text className={labelClass}>СНИЛС</Text>
          <MaskedTextInput
            mask="999-999-999 99"
            className={textInputClass}
            placeholder="000-000-000 00"
            keyboardType="numeric"
            style={webOutlineNone}
            value={snils}
            onChangeText={(_: string, unmasked: string) => handleChange("snils", unmasked)}
          />
          <Text className={labelClass}>Номер водительского удостоверения</Text>
          <MaskedTextInput
            mask="99 99 999999"
            className={textInputClass}
            placeholder="00 00 000000"
            style={webOutlineNone}
            keyboardType="numeric"
            value={driver_license}
            onChangeText={(_: string, unmasked: string) => handleChange("driver_license", unmasked)}
          />
          <Text className={labelClass}>Дата выдачи ВУ</Text>
          <DatePickerField
            placeholder="Введите дату выдачи ВУ"
            className={textInputClass}
            value={driver_license_date}
            onChange={(value) => handleChange("driver_license_date", value)}
          />
          <CustomCheckbox
            onChange={() => handleChange("consent", !consent)}
            className="flex-row items-center my-6"
            value={consent}
            label="Данные физ.лица переданы с его согласия"
          />
          <CustomButton
            title="Проверить"
            type="primary"
            onPress={handleSubmit}
            disabled={isButtonDisabled}
          />
        </>
      ) : (
        <View className="flex flex-col items-center justify-center mt-96">
          <ActivityIndicator />
          <Text>Готовим данные, еще чуть-чуть...</Text>
        </View>
      )}
    </ScrollView>
  );
};

export default IndividualCheckForm;
